import React from 'react';
import {
  Avatar,
  Box,
  Button,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  ContactPhone as ContactIcon,
  ContentCopy as CopyIcon,
  Download as DownloadIcon,
  InsertDriveFile as DocumentIcon,
  LocationOn as LocationIcon,
  Mic as MicIcon,
  OpenInNew as OpenInNewIcon,
  Payments as PixIcon,
  Phone as PhoneIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { AtendimentoMensagem } from '../../../services/atendimentoService';
import {
  downloadMediaFile,
  formatFileSize,
  formatPixAmount,
  getFileNameFromUrl,
  getMapsUrl,
  getMediaSrc,
  getWhatsAppContactUrl,
  parseContactMessage,
  parseLocationMessage,
  parsePixMessage,
} from '../utils/mensagemConteudoUtils';

export interface PreviewImagePayload {
  src: string;
  name?: string | null;
  caption?: string | null;
}

export interface AtendimentoMensagemConteudoProps {
  msg: AtendimentoMensagem;
  isMine: boolean;
  isPrivate: boolean;
  onOpenImage?: (payload: PreviewImagePayload) => void;
  onConsultarViabilidade?: (coords: { lat: number; lng: number }) => void;
}

const AtendimentoMensagemConteudo: React.FC<AtendimentoMensagemConteudoProps> = ({
  msg,
  isMine,
  isPrivate,
  onOpenImage,
  onConsultarViabilidade,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const mediaSrc = getMediaSrc(msg);
  const isStickerMessage = msg.tipo === 'sticker';
  const isImageMessage = msg.tipo === 'image' || (isStickerMessage && msg.media_tipo?.startsWith('image/'));
  const isVideoMessage = msg.tipo === 'video' || msg.media_tipo?.startsWith('video/');
  const isAudioMessage = msg.tipo === 'audio' || msg.media_tipo?.startsWith('audio/');
  const isDocumentMessage = msg.tipo === 'document';
  const location = msg.tipo === 'location' ? parseLocationMessage(msg) : null;
  const sharedContact = msg.tipo === 'contact' ? parseContactMessage(msg) : null;
  const pixData = msg.tipo === 'pix' ? parsePixMessage(msg) : null;
  const hasText = Boolean(msg.corpo?.trim());
  const mediaName = msg.media_nome || (mediaSrc ? getFileNameFromUrl(mediaSrc) : null);
  const mediaSize = formatFileSize(msg.media_tamanho);

  if (isImageMessage && mediaSrc) {
    return (
      <Box>
        <Box
          component="img"
          src={mediaSrc}
          alt={msg.media_nome || msg.corpo || 'Imagem enviada'}
          onClick={() => onOpenImage?.({ src: mediaSrc, name: msg.media_nome || getFileNameFromUrl(mediaSrc), caption: msg.corpo })}
          sx={{
            display: 'block',
            maxWidth: isStickerMessage ? { xs: 118, sm: 132 } : { xs: 240, sm: 320 },
            maxHeight: isStickerMessage ? 132 : 360,
            width: 'auto',
            height: 'auto',
            objectFit: 'contain',
            borderRadius: isStickerMessage ? 0 : 1.5,
            cursor: onOpenImage ? 'zoom-in' : 'default',
            bgcolor: isStickerMessage ? 'transparent' : alpha(theme.palette.common.black, 0.06),
            filter: isStickerMessage && isDark ? 'drop-shadow(0 1px 2px rgba(0,0,0,0.45))' : 'none',
          }}
        />
        {hasText && (
          <Typography variant="body2" sx={{ mt: 0.75, lineHeight: 1.5, fontSize: '0.875rem', wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
            {msg.corpo}
          </Typography>
        )}
      </Box>
    );
  }

  if (isVideoMessage && mediaSrc) {
    return (
      <Box>
        <Box
          component="video"
          src={mediaSrc}
          controls
          sx={{
            display: 'block',
            maxWidth: { xs: 240, sm: 320 },
            maxHeight: 360,
            width: '100%',
            borderRadius: 1.5,
            bgcolor: alpha(theme.palette.common.black, 0.06),
          }}
        />
        {(mediaName || mediaSize) && (
          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', fontSize: '0.68rem', mt: 0.35 }}>
            {[mediaName || 'Vídeo', mediaSize].filter(Boolean).join(' - ')}
          </Typography>
        )}
        {hasText && (
          <Typography variant="body2" sx={{ mt: 0.75, lineHeight: 1.5, fontSize: '0.875rem', wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
            {msg.corpo}
          </Typography>
        )}
      </Box>
    );
  }

  if (isAudioMessage && mediaSrc) {
    return (
      <Box sx={{ width: { xs: 250, sm: 320 }, maxWidth: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            p: 1,
            borderRadius: 1.5,
            bgcolor: isDark ? alpha(theme.palette.common.white, 0.07) : alpha(theme.palette.common.black, 0.04),
            border: `1px solid ${isDark ? alpha(theme.palette.common.white, 0.08) : alpha(theme.palette.common.black, 0.06)}`,
          }}
        >
          <MicIcon sx={{ fontSize: 20, color: isMine ? theme.palette.success.dark : theme.palette.primary.main, flexShrink: 0 }} />
          <Box minWidth={0} flex={1}>
            <Box component="audio" controls src={mediaSrc} sx={{ width: '100%', height: 34, display: 'block' }} />
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', fontSize: '0.68rem', mt: 0.25 }}>
              {[mediaName || 'Áudio', mediaSize].filter(Boolean).join(' - ')}
            </Typography>
          </Box>
          <Tooltip title="Baixar áudio">
            <IconButton size="small" onClick={() => downloadMediaFile(mediaSrc, mediaName || 'audio-atendimento.webm')}>
              <DownloadIcon sx={{ fontSize: 17 }} />
            </IconButton>
          </Tooltip>
        </Box>
        {hasText && (
          <Typography variant="body2" sx={{ mt: 0.75, lineHeight: 1.5, fontSize: '0.875rem', wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
            {msg.corpo}
          </Typography>
        )}
      </Box>
    );
  }

  if (isDocumentMessage) {
    return (
      <Box sx={{ width: { xs: 250, sm: 320 }, maxWidth: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            p: 1,
            borderRadius: 1.5,
            bgcolor: isDark ? alpha(theme.palette.common.white, 0.07) : alpha(theme.palette.common.black, 0.04),
            border: `1px solid ${isDark ? alpha(theme.palette.common.white, 0.08) : alpha(theme.palette.common.black, 0.06)}`,
          }}
        >
          <Box
            sx={{
              width: 38,
              height: 42,
              borderRadius: 1,
              bgcolor: alpha(theme.palette.error.main, isDark ? 0.22 : 0.12),
              color: theme.palette.error.main,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <DocumentIcon sx={{ fontSize: 23 }} />
          </Box>
          <Box minWidth={0} flex={1}>
            <Typography variant="body2" noWrap sx={{ fontSize: '0.82rem', fontWeight: 700 }}>
              {mediaName || msg.corpo || 'Documento'}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', fontSize: '0.68rem', mt: 0.1 }}>
              {[msg.media_tipo || 'Documento', mediaSize].filter(Boolean).join(' - ')}
            </Typography>
          </Box>
          {mediaSrc && (
            <Tooltip title="Baixar documento">
              <IconButton size="small" onClick={() => downloadMediaFile(mediaSrc, mediaName || 'documento-atendimento')}>
                <DownloadIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        {mediaSrc && (
          <Button
            size="small"
            variant="outlined"
            endIcon={<OpenInNewIcon sx={{ fontSize: 15 }} />}
            onClick={() => window.open(mediaSrc, '_blank', 'noopener,noreferrer')}
            sx={{ mt: 0.75, textTransform: 'none', borderRadius: 1.5, fontSize: '0.72rem', py: 0.25 }}
          >
            Abrir documento
          </Button>
        )}
        {hasText && mediaName !== msg.corpo && (
          <Typography variant="body2" sx={{ mt: 0.75, lineHeight: 1.5, fontSize: '0.875rem', wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
            {msg.corpo}
          </Typography>
        )}
      </Box>
    );
  }

  if (location) {
    return (
      <Box sx={{ width: { xs: 250, sm: 320 }, maxWidth: '100%' }}>
        <Box
          sx={{
            height: 108,
            borderRadius: 1.5,
            overflow: 'hidden',
            position: 'relative',
            bgcolor: isDark ? alpha(theme.palette.common.white, 0.08) : '#e8f1ec',
            border: `1px solid ${isDark ? alpha(theme.palette.common.white, 0.08) : alpha(theme.palette.success.main, 0.18)}`,
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              backgroundImage: `
                linear-gradient(90deg, ${alpha(theme.palette.success.main, isDark ? 0.13 : 0.16)} 1px, transparent 1px),
                linear-gradient(${alpha(theme.palette.success.main, isDark ? 0.13 : 0.16)} 1px, transparent 1px)
              `,
              backgroundSize: '28px 28px',
              opacity: 0.8,
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: `radial-gradient(circle at 50% 48%, ${alpha(theme.palette.success.main, 0.22)}, transparent 34%)`,
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -58%)',
              width: 42,
              height: 42,
              borderRadius: '50%',
              bgcolor: theme.palette.error.main,
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 8px 20px rgba(0,0,0,0.22)',
            }}
          >
            <LocationIcon sx={{ fontSize: 25 }} />
          </Box>
        </Box>
        <Box mt={0.85}>
          <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 700, lineHeight: 1.3 }}>
            {location.name || 'Localização compartilhada'}
          </Typography>
          {location.address && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, lineHeight: 1.35 }}>
              {location.address}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, fontSize: '0.68rem' }}>
            {location.lat.toFixed(6)}, {location.lng.toFixed(6)}
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={0.6} mt={0.8}>
            <Button
              size="small"
              variant="outlined"
              endIcon={<OpenInNewIcon sx={{ fontSize: 15 }} />}
              onClick={() => window.open(getMapsUrl(location), '_blank', 'noopener,noreferrer')}
              sx={{ textTransform: 'none', borderRadius: 1.5, fontSize: '0.72rem', py: 0.25 }}
            >
              Abrir no mapa
            </Button>
            {!isMine && onConsultarViabilidade && (
              <Button
                size="small"
                variant="contained"
                color="primary"
                startIcon={<SearchIcon sx={{ fontSize: 15 }} />}
                onClick={() => onConsultarViabilidade({ lat: location.lat, lng: location.lng })}
                sx={{ textTransform: 'none', borderRadius: 1.5, fontSize: '0.72rem', py: 0.25 }}
              >
                Consultar viabilidade
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    );
  }

  if (sharedContact) {
    return (
      <Box sx={{ width: { xs: 250, sm: 320 }, maxWidth: '100%' }}>
        <Box display="flex" alignItems="center" gap={1.2}>
          <Avatar
            sx={{
              width: 42,
              height: 42,
              bgcolor: isMine ? alpha(theme.palette.success.dark, 0.18) : alpha(theme.palette.primary.main, 0.14),
              color: isMine ? theme.palette.success.dark : theme.palette.primary.main,
              flexShrink: 0,
            }}
          >
            <ContactIcon sx={{ fontSize: 22 }} />
          </Avatar>
          <Box minWidth={0} flex={1}>
            <Typography variant="body2" noWrap sx={{ fontSize: '0.88rem', fontWeight: 700 }}>
              {sharedContact.name}
            </Typography>
            {sharedContact.number && (
              <Typography variant="body2" color="text.secondary" noWrap sx={{ fontSize: '0.78rem', mt: 0.15 }}>
                {sharedContact.number}
              </Typography>
            )}
            {sharedContact.email && (
              <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', fontSize: '0.7rem', mt: 0.15 }}>
                {sharedContact.email}
              </Typography>
            )}
          </Box>
        </Box>
        {sharedContact.number && (
          <Box display="flex" alignItems="center" gap={0.75} mt={1}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<PhoneIcon sx={{ fontSize: 15 }} />}
              onClick={() => window.open(getWhatsAppContactUrl(sharedContact.number!), '_blank', 'noopener,noreferrer')}
              sx={{ textTransform: 'none', borderRadius: 1.5, fontSize: '0.72rem', py: 0.25 }}
            >
              Abrir conversa
            </Button>
            <Tooltip title="Copiar telefone">
              <IconButton
                size="small"
                onClick={() => {
                  navigator.clipboard.writeText(sharedContact.number || '').then(() => toast.success('Telefone copiado.'));
                }}
              >
                <CopyIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>
    );
  }

  if (pixData) {
    return (
      <Box sx={{ width: { xs: 230, sm: 280 }, maxWidth: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.2,
            p: 1.1,
            borderRadius: 1.5,
            bgcolor: isDark ? alpha('#32BCAD', 0.12) : alpha('#32BCAD', 0.1),
            border: `1px solid ${alpha('#32BCAD', 0.3)}`,
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: '#32BCAD',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <PixIcon sx={{ fontSize: 22 }} />
          </Box>
          <Box minWidth={0} flex={1}>
            <Typography variant="body2" sx={{ fontSize: '0.82rem', fontWeight: 700, lineHeight: 1.25 }}>
              {pixData.label}
            </Typography>
            {formatPixAmount(pixData.amount, pixData.currency) ? (
              <Typography variant="body2" sx={{ fontSize: '0.95rem', fontWeight: 800, color: isDark ? '#5fd6c7' : '#1f8a7e', mt: 0.15 }}>
                {formatPixAmount(pixData.amount, pixData.currency)}
              </Typography>
            ) : (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.15 }}>
                Valor definido pelo pagador
              </Typography>
            )}
            {pixData.merchantName && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.15, lineHeight: 1.35 }}>
                Recebedor: {pixData.merchantName}
              </Typography>
            )}
            {pixData.note && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.15, lineHeight: 1.35 }}>
                {pixData.note}
              </Typography>
            )}
          </Box>
        </Box>
        {pixData.pixKey && (
          <Box display="flex" alignItems="center" gap={0.75} mt={0.85}>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.7rem', flex: 1 }}>
              Chave{pixData.keyType ? ` (${pixData.keyType})` : ''}: {pixData.pixKey}
            </Typography>
            <Tooltip title="Copiar chave Pix">
              <IconButton
                size="small"
                onClick={() => {
                  navigator.clipboard.writeText(pixData.pixKey || '').then(() => toast.success('Chave Pix copiada.'));
                }}
              >
                <CopyIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Typography variant="body2" sx={{ lineHeight: 1.5, fontSize: '0.875rem', wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
      {hasText ? msg.corpo : msg.tipo === 'text' || msg.tipo === 'expediente' || isPrivate ? '' : `[${msg.tipo}]`}
    </Typography>
  );
};

export default AtendimentoMensagemConteudo;
